/**
 * Convx homepage v3 (20 Oct 2026). Deferred, homepage only (layout/theme.liquid).
 *
 * 1. One gender for the page. Default women; ?g=m opens on men; the choice is kept for the visit in sessionStorage
 *    (key convx_home_g). Any switch or tab sets Shop by, the three rails, The Originals and Cloudsteppers together,
 *    plus every gender-following CTA ([data-convx-cta] with data-w / data-m).
 * 2. Men's rail cards are not in the HTML. On the first switch to men (or on load when men is the saved choice) it
 *    fetches one Section Rendering API request per rail, in parallel, and keeps the first N cards per rail whose
 *    handle no earlier men's rail already shows. Nothing is prefetched for the other gender after load. If any rail
 *    fails, that rail shows a plain link and the next switch to men tries again.
 * 3. Rail arrows (desktop), tab keyboard support, and Flockler lazy loading for What's Trending.
 *
 * No tracking code here: every analytics event lives in GTM, which reads data-convx-list / data-convx-pos.
 */
(function () {
  'use strict';

  var KEY = 'convx_home_g';
  var root = document.documentElement;
  var FEED = 'convx-home-rail-feed';
  var menPromise = null;
  var arrowFrame = 0;

  function readG() {
    var m = /[?&]g=(m|w)(?:&|$)/.exec(location.search);
    if (m) return m[1];
    try {
      return sessionStorage.getItem(KEY) === 'm' ? 'm' : 'w';
    } catch (e) {
      return 'w';
    }
  }

  /* ---------------------------------------------------------------- gender state */
  function setG(g) {
    g = g === 'm' ? 'm' : 'w';
    root.setAttribute('data-convx-g', g);
    document.querySelectorAll('[data-convx-set-g]').forEach(function (el) {
      var on = el.getAttribute('data-convx-set-g') === g;
      if (el.getAttribute('role') === 'tab') {
        el.setAttribute('aria-selected', on ? 'true' : 'false');
        el.setAttribute('tabindex', on ? '0' : '-1');
      } else {
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    });
    document.querySelectorAll('[data-convx-cta]').forEach(function (a) {
      var href = g === 'm' ? a.getAttribute('data-m') : a.getAttribute('data-w');
      if (href) a.setAttribute('href', href);
    });
    try {
      sessionStorage.setItem(KEY, g);
    } catch (e) {}
    if (g === 'm') loadMen();
    scheduleArrows();
  }

  /* ---------------------------------------------------------------- men's rails */
  /* Same classes as a real card (title, gender line, price block), so the skeleton takes the card's own heights. */
  function skeleton(count) {
    var one =
      '<div class="convx-skel" aria-hidden="true"><div class="convx-skel__img"></div>' +
      '<div class="product-item__info"><div class="product-item-meta">' +
      '<span class="product-item-meta__title heading">&nbsp;</span>' +
      '<p class="gender-occasion-text">&nbsp;</p>' +
      '<div class="product-item-meta__price-list-container"><div class="price-list"></div></div>' +
      '<div class="product-item-meta__swatch-list color-swatch-list color-swatch-list--mini color-swatch-list--capped">' +
      '<div class="color-swatch"><span class="color-swatch__item"></span></div>' +
      '<div class="color-swatch"><span class="color-swatch__item"></span></div>' +
      '<div class="color-swatch"><span class="color-swatch__item"></span></div></div>' +
      '</div></div></div>';
    var html = '';
    for (var i = 0; i < count; i++) html += one;
    return html;
  }

  function feedUrl(rail) {
    var url = '/collections/' + rail.getAttribute('data-collection-m') + '?section_id=' + FEED;
    if (rail.getAttribute('data-gender-filter') === 'true') url += '&filter.p.m.specifications.gender=Mens';
    var preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url += '&preview_theme_id=' + encodeURIComponent(preview);
    return url;
  }

  function fetchRail(rail) {
    return fetch(feedUrl(rail), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var cards = Array.prototype.slice.call(doc.querySelectorAll('.convx-feed .product-item'));
        if (!cards.length) throw new Error('empty feed');
        return cards;
      });
  }

  /* Where the shopper can still go when the feed fails: the rail's own men's button URL (gender-filtered for the sale). */
  function fallbackNode(rail) {
    var cta = rail.querySelector('[data-convx-cta]');
    var href =
      (cta && cta.getAttribute('data-m')) ||
      rail.getAttribute('data-collection-m-url') ||
      '/collections/' + rail.getAttribute('data-collection-m');
    var box = document.createElement('div');
    box.className = 'convx-rail__fallback';
    var line = document.createElement('p');
    line.textContent = 'Could not load men’s styles.';
    var link = document.createElement('a');
    link.href = href;
    link.textContent = rail.getAttribute('data-fallback') || 'Shop men’s styles';
    box.appendChild(line);
    box.appendChild(link);
    return box;
  }

  function loadMen() {
    if (menPromise) return menPromise;
    var rails = Array.prototype.slice.call(document.querySelectorAll('.convx-rail'));
    if (!rails.length) {
      menPromise = Promise.resolve();
      return menPromise;
    }
    rails.forEach(function (rail) {
      var track = rail.querySelector('.convx-rail__track[data-g="m"]');
      track.innerHTML = skeleton(parseInt(rail.getAttribute('data-count'), 10) || 8);
    });
    var requests = rails.map(function (rail) {
      return fetchRail(rail).then(
        function (cards) { return { cards: cards }; },
        function () { return { failed: true }; }
      );
    });
    var promise = Promise.all(requests).then(function (results) {
      var seen = {};
      var anyFailed = false;
      rails.forEach(function (rail, i) {
        var track = rail.querySelector('.convx-rail__track[data-g="m"]');
        var result = results[i];
        var count = parseInt(rail.getAttribute('data-count'), 10) || 8;
        var list = rail.getAttribute('data-convx-rail');
        track.innerHTML = '';
        if (result.failed) {
          anyFailed = true;
          track.appendChild(fallbackNode(rail));
          return;
        }
        var kept = 0;
        result.cards.forEach(function (card) {
          if (kept >= count) return;
          var handle = card.getAttribute('data-handle');
          if (seen[handle]) return;
          seen[handle] = true;
          kept += 1;
          card.setAttribute('data-convx-list', list);
          card.setAttribute('data-convx-pos', String(kept));
          track.appendChild(document.importNode(card, true));
        });
      });
      // a failure is not cached: the next switch to men fetches again
      if (anyFailed && menPromise === promise) menPromise = null;
      scheduleArrows();
    });
    menPromise = promise;
    return promise;
  }

  /* ---------------------------------------------------------------- rail arrows (desktop) */
  /* The two genders' tracks share one pair of buttons, so only the visible track may drive them. The hidden track has
     zero width and would always read as "at the end". */
  function visibleTrack(wrap) {
    var tracks = wrap.querySelectorAll('.convx-rail__track');
    for (var i = 0; i < tracks.length; i++) {
      if (tracks[i].offsetParent !== null) return tracks[i];
    }
    return null;
  }

  function updateArrows(wrap) {
    var prev = wrap.querySelector('[data-convx-arrow="prev"]');
    var next = wrap.querySelector('[data-convx-arrow="next"]');
    if (!prev || !next) return;
    var track = visibleTrack(wrap);
    if (!track) return;
    var max = track.scrollWidth - track.clientWidth - 2;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = max <= 0 || track.scrollLeft >= max;
  }

  function updateAllArrows() {
    arrowFrame = 0;
    document.querySelectorAll('.convx-rail__wrap').forEach(updateArrows);
  }

  /* after layout has settled (gender change, men's cards inserted, resize) */
  function scheduleArrows() {
    if (arrowFrame) return;
    arrowFrame = requestAnimationFrame(function () {
      arrowFrame = requestAnimationFrame(updateAllArrows);
    });
  }

  /* ---------------------------------------------------------------- events */
  document.addEventListener('click', function (e) {
    var set = e.target.closest('[data-convx-set-g]');
    if (set) {
      setG(set.getAttribute('data-convx-set-g'));
      return;
    }
    var arrow = e.target.closest('[data-convx-arrow]');
    if (arrow) {
      var wrap = arrow.closest('.convx-rail__wrap');
      var track = wrap && visibleTrack(wrap);
      if (track) {
        var dir = arrow.getAttribute('data-convx-arrow') === 'next' ? 1 : -1;
        track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: 'smooth' });
      }
    }
  });

  /* ---------------------------------------------------------------- card swatches */
  /* Focal's ProductItem already rewrites the card links with ?variant=<id> on this same change event. The photo swap is
     ours: no hidden photo per colour, one CDN URL per swatch (data-convx-img), re-cut to the card's own srcset widths. */
  function swapCardPhoto(card, url) {
    /* staleness token: only the latest tap on this card may set the photo, a late load of an earlier colour is dropped */
    card._convxSwap = (card._convxSwap || 0) + 1;
    var token = card._convxSwap;
    var img = card.querySelector('img.product-item__primary-image');
    if (!img || !url) return;
    var widths = [];
    (img.getAttribute('srcset') || '').replace(/(\d+)w/g, function (m, w) { widths.push(parseInt(w, 10)); });
    if (!widths.length) widths = [300, 400, 500, 600, 800, 1000];
    var base = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    var srcset = widths.map(function (w) { return base + sep + 'width=' + w + ' ' + w + 'w'; }).join(', ');
    var src = base + sep + 'width=' + (widths.indexOf(600) !== -1 ? 600 : widths[Math.floor(widths.length / 2)]);
    var pre = new Image();
    pre.onload = function () {
      if (card._convxSwap !== token) return;
      img.srcset = srcset;
      img.src = src;
    };
    /* on an error the current photo stays and the links stay on the tapped colour (Focal rewrote them on the same change) */
    pre.sizes = img.sizes;
    pre.srcset = srcset;
    pre.src = src;
  }

  document.addEventListener('change', function (e) {
    var radio = e.target.closest && e.target.closest('.color-swatch__radio[data-convx-swatch]');
    if (!radio) return;
    var card = radio.closest('.product-item');
    if (card) swapCardPhoto(card, radio.getAttribute('data-convx-img'));
  });

  /* Women and Men tabs: arrow keys, Home and End move between the two tabs (the selected one is the only tab stop) */
  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[role="tab"][data-convx-set-g]');
    if (!tab) return;
    var key = e.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;
    var tabs = Array.prototype.slice.call(tab.parentNode.querySelectorAll('[role="tab"]'));
    var i = tabs.indexOf(tab);
    var j = key === 'Home' ? 0 : key === 'End' ? tabs.length - 1 : (i + (key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    e.preventDefault();
    var target = tabs[j];
    setG(target.getAttribute('data-convx-set-g'));
    target.focus();
  });

  /* start the men's requests as soon as a Men control is pressed or focused, a beat before the click lands */
  function early(e) {
    var set = e.target.closest && e.target.closest('[data-convx-set-g="m"]');
    if (set) loadMen();
  }
  document.addEventListener('pointerdown', early, true);
  document.addEventListener('focusin', early);

  document.addEventListener(
    'scroll',
    function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('convx-rail__track') && t.offsetParent !== null) {
        var wrap = t.closest('.convx-rail__wrap');
        if (wrap) updateArrows(wrap);
      }
    },
    true
  );

  window.addEventListener('resize', scheduleArrows);
  window.addEventListener('load', scheduleArrows);

  /* ---------------------------------------------------------------- What's Trending (Flockler) lazy loader */
  function initFlockler() {
    var nodes = document.querySelectorAll('[data-convx-flockler]');
    if (!nodes.length) return;
    function load(node) {
      if (node.getAttribute('data-loaded')) return;
      node.setAttribute('data-loaded', '1');
      var s = document.createElement('script');
      s.src = node.getAttribute('data-convx-flockler');
      s.async = true;
      document.body.appendChild(s);
    }
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(load);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            io.unobserve(en.target);
            var embed = en.target.querySelector('[data-convx-flockler]');
            if (embed) load(embed);
          }
        });
      },
      { rootMargin: '400px 0px' }
    );
    nodes.forEach(function (n) { io.observe(n.closest('.shopify-section') || n); });
  }

  setG(readG());
  initFlockler();
})();
